import { type FC, type PropsWithChildren, type SyntheticEvent, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, Modal, type ModalProps, useIsMobileVariant } from '@dhl-ui/components'

export type ConfirmationModalProps = Omit<ModalProps, 'children'> &
  PropsWithChildren<{
    titleText: string
    acceptText?: string
    rejectText?: string
    isAcceptButtonDisabled?: boolean
    isAccepting?: boolean
    onAccept: () => void | Promise<void>
    onOpen?: () => void
    onClose?: () => void
    onReject?: () => void
  }> & {
    /**
     * When you do not want consumers to close the modal when they press escape key
     * @default false
     */
    disableEscapeKeyDown?: boolean
  }

/**
 * Standard DHL Confirmation modal
 * By default it will have default translations but you are able to ask your own confirmation
 * There are handlers in which you can have influence when user is closing, accepting or rejecting the confirmation
 */
export const ConfirmationModal: FC<ConfirmationModalProps> = ({
  titleText,
  acceptText,
  rejectText,
  open,
  children,
  isAccepting,
  isAcceptButtonDisabled,
  disableEscapeKeyDown = false,
  onAccept,
  onClose,
  onOpen,
  onReject,
  ...rest
}) => {
  const { t } = useTranslation()
  const isMobileVariant = useIsMobileVariant()

  const labelReject = rejectText || t('components.confirmationDialog.reject') || 'Annuleren'
  const labelProceed = acceptText || t('components.confirmationDialog.proceed') || 'Ok'

  useEffect(() => {
    if (open) {
      onOpen?.()
    }
  }, [onOpen, open])

  const handleAccept = () => {
    onAccept()
    onClose?.()
  }

  const handleReject = () => {
    onReject?.()
    onClose?.()
  }

  const handleClose = (_event: SyntheticEvent<unknown>, reason: 'backdropClick' | 'escapeKeyDown' | undefined) => {
    // Prevent closing when escape key is disabled
    if (disableEscapeKeyDown && reason === 'escapeKeyDown') {
      return
    }

    handleReject()
  }

  return (
    <Modal isSwipeable open={open} onClose={handleClose} {...rest}>
      <Modal.Header title={titleText} />

      {children && <Modal.Content>{children}</Modal.Content>}
      <Modal.Footer>
        <Button
          data-testid="confirmation-accept"
          disabled={isAcceptButtonDisabled}
          fullWidth={isMobileVariant}
          loading={isAccepting}
          size={isMobileVariant ? 'large' : 'medium'}
          sx={{ order: isMobileVariant ? 1 : 2 }}
          variant="contained"
          onClick={handleAccept}
        >
          {labelProceed}
        </Button>

        <Button
          color="dark"
          data-testid="confirmation-reject"
          disabled={isAccepting}
          fullWidth={isMobileVariant}
          size={isMobileVariant ? 'large' : 'medium'}
          sx={{ order: isMobileVariant ? 2 : 1, ml: isMobileVariant ? undefined : 'auto' }}
          variant="text"
          onClick={handleReject}
        >
          {labelReject}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
